'use client';

import { useState, useContext } from 'react';
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, PhoneIcon, PlayCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import appLogoDark from '../../assets/appLogoFinalDark.png';
import { AuthContext } from '../../context/AuthContext';

const companyItems = [
  { name: 'About Us', description: 'Learn about our mission and team', href: 'https://transtechsys.com/', icon: PlayCircleIcon },
  { name: 'Contact Sales', description: 'Get in touch with our sales team', href: 'https://transtechsys.com/products/salesRequest', icon: PhoneIcon },
  { name: 'Whitelist', description: 'Ensure you receive our emails', href: '/whitelist', icon: UserGroupIcon, isInternal: true },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, loading } = useContext(AuthContext);

  if (loading) {
    return null;
  }

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-black shadow-md">
      <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8">
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5">
            <span className="sr-only">TransTech Connect</span>
            <img src={appLogoDark} alt="TransTech Connect Logo" className="h-8 w-auto" />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-8">
          <Link to="/" className="text-sm/6 font-semibold text-white hover:text-yellow-500 transition duration-300">
            Home
          </Link>
          <Link to="/integrations" className="text-sm/6 font-semibold text-white hover:text-yellow-500 transition duration-300">
            Integrations
          </Link>
          <Link to="/pricing" className="text-sm/6 font-semibold text-white hover:text-yellow-500 transition duration-300">
            Pricing
          </Link>
          <Popover className="relative">
            <PopoverButton className="flex items-center gap-x-1 text-sm/6 font-semibold text-white hover:text-yellow-500 focus:outline-none">
              Company
              <ChevronDownIcon aria-hidden="true" className="size-5 flex-none text-gray-400" />
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-stone-800 shadow-lg ring-1 ring-stone-700/30 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="p-4">
                {companyItems.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-stone-700"
                  >
                    <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-stone-700 group-hover:bg-stone-600">
                      <item.icon aria-hidden="true" className="size-6 text-yellow-500 group-hover:text-yellow-600" />
                    </div>
                    <div className="flex-auto">
                      {item.isInternal ? (
                        <Link to={item.href} className="block font-semibold text-white">
                          {item.name}
                          <span className="absolute inset-0" />
                        </Link>
                      ) : (
                        <a href={item.href} target="_blank" rel="noopener noreferrer" className="block font-semibold text-white">
                          {item.name}
                          <span className="absolute inset-0" />
                        </a>
                      )}
                      <p className="mt-1 text-gray-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          {isAuthenticated ? (
            <Link to="/dashboard" className="text-sm/6 font-semibold text-white hover:text-yellow-500 transition duration-300">
              Go to Dashboard <span aria-hidden="true">→</span>
            </Link>
          ) : (
            <Link to="/login" className="text-sm/6 font-semibold text-white hover:text-yellow-500 transition duration-300">
              Log in <span aria-hidden="true">→</span>
            </Link>
          )}
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-gray-800 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-700/20">
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">TransTech Connect</span>
              <img src={appLogoDark} alt="TransTech Connect Logo" className="h-8 w-auto" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-white"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-700/20">
              <div className="space-y-2 py-6">
                <Link
                  to="/"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                >
                  Home
                </Link>
                <Link
                  to="/integrations"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                >
                  Integrations
                </Link>
                <Link
                  to="/pricing"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                >
                  Pricing
                </Link>
                <Disclosure as="div" className="-mx-3">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500">
                    Company
                    <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-2">
                    {companyItems.map((item) => (
                      item.isInternal ? (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <DisclosureButton
                          key={item.name}
                          as="a"
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                        >
                          {item.name}
                        </DisclosureButton>
                      )
                    ))}
                  </DisclosurePanel>
                </Disclosure>
              </div>
              <div className="py-6">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                  >
                    Go to Dashboard <span aria-hidden="true">→</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-700 hover:text-yellow-500 transition duration-300"
                  >
                    Log in <span aria-hidden="true">→</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}